import { METRICS, type MetricType } from 'features/charts-v2/types'

/**
 * Maps each average-grain metric to the count metric it must be weighted by
 * when rolling up across rows. `null` marks a metric that is summed rather than
 * averaged (the count metrics themselves).
 *
 * sentiment = AVG(post_text_sentiment) is sentence-grain, so it weights by
 * sentence_count = COUNT(post_text). rating / net_promoter_score = AVG(post_score)
 * are post-grain, so they weight by record_count = COUNT(post). This is the same
 * grain relationship the bubble chart encodes in `getWeightMetricForAxis`.
 */
export const METRIC_WEIGHT_BY: Record<MetricType, MetricType | null> = {
  [METRICS.RECORD_COUNT.id]: null,
  [METRICS.SENTENCE_COUNT.id]: null,
  [METRICS.SENTIMENT.id]: METRICS.SENTENCE_COUNT.id,
  [METRICS.RATING.id]: METRICS.RECORD_COUNT.id,
  [METRICS.NET_PROMOTER_SCORE.id]: METRICS.RECORD_COUNT.id,
}

/**
 * Augment the user's selected metrics with any hidden weight metric the totals
 * math needs, appended LAST so it never shifts a column the UI iterates by
 * positional index (rendered columns, CSV export, and "% of Total" all iterate
 * the user's selection, not this augmented list).
 *
 * Rules:
 * - If `sentiment` is selected and `sentence_count` is not already present,
 *   append `sentence_count` (its weight metric).
 * - Preserve the legacy behavior of fetching a companion volume metric for a
 *   sole `rating` selection by appending `record_count` when it is the only
 *   metric and not already present.
 * - Idempotent: a metric already in the list is never appended again.
 *
 * The returned positional list is the single source of truth for the augmented
 * request (`use-chart-query`) AND the totals math, so the hidden weight column's
 * index in the backend response is the same on both seams.
 */
export function augmentMetricsForWeighting(
  selectedMetrics: MetricType[],
): MetricType[] {
  const augmented = [...selectedMetrics]

  const appendIfMissing = (metric: MetricType): void => {
    if (!augmented.includes(metric)) {
      augmented.push(metric)
    }
  }

  if (augmented.includes(METRICS.SENTIMENT.id)) {
    appendIfMissing(METRICS.SENTENCE_COUNT.id)
  }

  // Legacy: a lone rating selection fetches record_count so the legend can show
  // a "% of Volume" column.
  if (
    selectedMetrics.length === 1 &&
    selectedMetrics[0] === METRICS.RATING.id
  ) {
    appendIfMissing(METRICS.RECORD_COUNT.id)
  }

  return augmented
}
